import { useVideoMetadata } from '@hooks/api';
import { useDialog } from '@hooks/useDialog';
import { formatTimestamp } from '@utils/timeUtils';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from 'universal-scripts';

import './VideoAnalysisDialog.css';

const videoPlaceholder = 'https://vetric-svc.s3.eu-west-1.amazonaws.com/images/thumbnails/placeholder.svg';

type TabType = 'description' | 'claims';

interface VideoAnalysisDialogProps {
	isOpen: boolean;
	onClose: () => void;
	initialTab?: TabType;
}

export default function VideoAnalysisDialog({ isOpen, onClose, initialTab = 'description' }: VideoAnalysisDialogProps) {
	const { dialogRef, handleBackdropClick } = useDialog(isOpen, onClose);

	const [activeTab, setActiveTab] = useState<TabType>(initialTab);

	const videoUrl = useAppSelector((state) => state.sentinel.videoUrl);
	const { videoDescription, allClaims } = useAppSelector((state) => state.sentinel.phases.detect);
	const metadata = useVideoMetadata(videoUrl);

	useEffect(() => {
		if (isOpen) setActiveTab(initialTab);
	}, [initialTab, isOpen]);

	if (!isOpen) return null;

	return (
		<dialog ref={dialogRef} className="sentinel-dialog" onClick={handleBackdropClick} onCancel={onClose} id="video-analysis-dialog">
			<header>
				<h2>
					<span>
						<FormattedMessage id="sentinel.video-analysis-dialog.title" />
					</span>
				</h2>
				<button className="dialog-close icon icon-cross" onClick={onClose} />
			</header>

			<div className="split">
				<div className="item item-left">
					<div
						style={{ backgroundImage: `url('${metadata?.thumbnailUrl ? metadata?.thumbnailUrl : videoPlaceholder}')` }}
						className="image-wrapper"
					/>
				</div>
				<div className="item item-right">
					<div className="tabs">
						<button className={`tab ${activeTab === 'description' ? 'active' : ''}`} onClick={() => setActiveTab('description')}>
							<FormattedMessage id="sentinel.video-description.title" />
						</button>
						<button className={`tab ${activeTab === 'claims' ? 'active' : ''}`} onClick={() => setActiveTab('claims')}>
							<FormattedMessage id="sentinel.video-detected-claims.title" /> ({allClaims.length})
						</button>
					</div>
					<div className="tab-content">
						{activeTab === 'description' && (
							<div className="description-content">
								<p>{videoDescription}</p>
							</div>
						)}

						{activeTab === 'claims' && (
							<div className="claims-content">
								<ul className="claims-list">
									{allClaims.map((claim, i) => {
										return (
											<li key={i}>
												<span>
													<strong>
														<span>{formatTimestamp(claim.timestamp_range.start.minutes, claim.timestamp_range.start.seconds)}</span>
													</strong>
													<span title={claim.claim}>{claim.claim}</span>
												</span>
											</li>
										);
									})}
								</ul>
							</div>
						)}
					</div>
				</div>
			</div>
		</dialog>
	);
}
